import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Crown, Rocket, Target, Lock, Check, X, Zap, TrendingUp } from 'lucide-react';

const UpgradeComparisonTable = ({ isOpen, onClose, onSelectPlan }) => {
  if (!isOpen) return null;
  
  const features = [
    { 
      name: 'Sparten & Templates', 
      free: '1 Test-Sparte', 
      starter: 'Alle 22 Sparten', 
      elite: 'Alle 22 + Updates',
      starterHas: true,
      eliteHas: true
    },
    { 
      name: 'KI-Creatives & Copy', 
      free: 'Mit Wasserzeichen', 
      starter: 'High-End (Clean)', 
      elite: 'High-End (Clean)',
      starterHas: true,
      eliteHas: true
    },
    { 
      name: 'Meta-Direkt-Sync', 
      free: 'Manuell (kompliziert)', 
      starter: 'Manuell (kompliziert)', 
      elite: 'Ein-Klick Live!',
      starterHas: false,
      eliteHas: true,
      isKiller: true
    },
    { 
      name: 'Business Manager nötig', 
      free: 'Ja (45+ Min)', 
      starter: 'Ja (45+ Min)', 
      elite: 'Nein! (Skip)',
      starterHas: false,
      eliteHas: true,
      isKiller: true
    },
    { 
      name: 'Targeting-Blueprints', 
      free: 'Gesperrt', 
      starter: 'Gesperrt', 
      elite: 'Alle Profi-Listen',
      starterHas: false,
      eliteHas: true
    },
    { 
      name: 'Agentur-Kosten Ersparnis', 
      free: '0 €', 
      starter: '~ 90%', 
      elite: '100% Ersparnis',
      starterHas: true,
      eliteHas: true
    },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
        style={{ backgroundColor: 'rgba(11, 14, 20, 0.95)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="max-w-4xl w-full rounded-2xl p-6 relative my-8"
          style={{ 
            backgroundColor: '#0B0E14',
            border: '1px solid rgba(255, 92, 0, 0.3)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Orange Accent Bar */}
          <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: 'linear-gradient(90deg, #FF5C00, #FF8A00, #FF5C00)' }} />
          
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#F5F5F7' }}>
              Deine Marketing-Freiheit: Wähle dein Level
            </h2>
            <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>
              Vom Test-User zum Agency-Killer in einem Schritt
            </p>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto mb-6">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left p-3 text-sm font-normal" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Feature</th>
                  <th className="p-3 text-center">
                    <div className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Test-User</div>
                    <div className="font-bold" style={{ color: '#F5F5F7' }}>Free</div>
                  </th>
                  <th className="p-3 text-center rounded-t-xl" style={{ backgroundColor: 'rgba(0, 242, 255, 0.05)' }}>
                    <div className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Starter</div>
                    <div className="font-bold" style={{ color: '#00F2FF' }}>19€ / 79€</div>
                  </th>
                  <th className="p-3 text-center rounded-t-xl" style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)', border: '2px solid rgba(255, 92, 0, 0.5)', borderBottom: 'none' }}>
                    <div className="px-2 py-0.5 text-xs font-bold rounded mb-1 inline-block" style={{ backgroundColor: '#FF5C00', color: 'white' }}>EMPFOHLEN</div>
                    <div className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Elite Lifetime</div>
                    <div className="font-bold" style={{ color: '#FF5C00' }}>299€ / 799€</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {features.map((feature, index) => (
                  <tr key={index} className={feature.isKiller ? '' : ''} style={feature.isKiller ? { backgroundColor: 'rgba(0, 242, 255, 0.03)' } : {}}>
                    <td className="p-3 text-sm" style={{ color: '#F5F5F7' }}>
                      {feature.isKiller && <Zap className="w-4 h-4 inline mr-1" style={{ color: '#00F2FF' }} />}
                      {feature.name}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <X className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.3)' }} />
                        <span className="text-xs" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>{feature.free}</span>
                      </div>
                    </td>
                    <td className="p-3 text-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.05)' }}>
                      <div className="flex items-center justify-center gap-1">
                        {feature.starterHas ? (
                          <Check className="w-4 h-4" style={{ color: '#00F2FF' }} />
                        ) : (
                          <X className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.3)' }} />
                        )}
                        <span className={`text-xs ${feature.starterHas ? '' : ''}`} style={{ color: feature.starterHas ? 'rgba(245, 245, 247, 0.8)' : 'rgba(245, 245, 247, 0.5)' }}>{feature.starter}</span>
                      </div>
                    </td>
                    <td className="p-3 text-center" style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)', borderLeft: '2px solid rgba(255, 92, 0, 0.5)', borderRight: '2px solid rgba(255, 92, 0, 0.5)' }}>
                      <div className="flex items-center justify-center gap-1">
                        <Check className="w-4 h-4" style={{ color: '#FF5C00' }} />
                        <span className="text-xs font-semibold" style={{ color: '#F5F5F7' }}>{feature.elite}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Agency Killer Calculator */}
          <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: 'rgba(0, 242, 255, 0.05)', border: '1px solid rgba(0, 242, 255, 0.2)' }}>
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8" style={{ color: '#00F2FF' }} />
              <div>
                <p className="font-semibold text-sm" style={{ color: '#F5F5F7' }}>Agency-Killer Rechnung:</p>
                <p className="text-xs" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
                  Du zahlst aktuell <span className="font-bold" style={{ color: '#F5F5F7' }}>2.500 €/Monat</span> für eine Agentur? 
                  FinAds Elite amortisiert sich für dich in <span className="font-bold" style={{ color: '#00F2FF' }}>genau 9 Tagen</span>.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="grid md:grid-cols-2 gap-4">
            <Button
              onClick={() => onSelectPlan('starter')}
              className="py-5 text-base font-bold rounded-xl"
              style={{ 
                backgroundColor: 'rgba(0, 242, 255, 0.1)',
                border: '1px solid rgba(0, 242, 255, 0.3)',
                color: '#00F2FF'
              }}
            >
              <Crown className="w-5 h-5 mr-2" />
              Starter ab 79€ wählen
            </Button>
            <Button
              onClick={() => onSelectPlan('elite')}
              className="py-5 text-base font-bold rounded-xl"
              style={{ 
                background: 'linear-gradient(135deg, #FF5C00 0%, #FF8A00 100%)',
                color: 'white',
                boxShadow: '0 0 30px rgba(255, 92, 0, 0.4)'
              }}
            >
              <Rocket className="w-5 h-5 mr-2" />
              Elite 799€ – Nie wieder Agentur!
            </Button>
          </div>

          <p className="text-center text-xs mt-4" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>
            Alle Pakete: Einmalzahlung. Keine Abo-Falle. Lebenslanger Zugang.
          </p>

          {/* Targeting Add-on Hint */}
          <div className="mt-4 p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(167, 139, 250, 0.1)', border: '1px solid rgba(167, 139, 250, 0.3)' }}>
            <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
              <Target className="w-4 h-4 inline mr-1" style={{ color: '#A78BFA' }} />
              <span className="font-semibold" style={{ color: '#A78BFA' }}>Zusatz-Modul:</span> Targeting-Helfer für{' '}
              <span className="font-bold" style={{ color: '#F5F5F7' }}>49 €</span> – Kopiere die exakten Zielgruppen der Top-Performer.
            </p>
          </div>

          <button
            onClick={onClose}
            className="absolute top-3 right-3 transition-colors p-2 rounded-full hover:bg-white/10"
            style={{ color: '#F5F5F7', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
          >
            <X className="w-6 h-6" />
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpgradeComparisonTable;
